import { useEffect, useState } from 'react'

import axios from 'axios'
import { getCookie } from 'cookies-next'

import { FileInCloud } from 'Root/App/Form/Upload'

export async function putPresignedObject(
  file: FileInCloud,
  namespace: string,
  completeCallback: Function,
  signal: AbortSignal,
  setError: (error: string) => void = () => {},
  setLoading: (loading: boolean) => void = () => {},
  setProgress: (progress: number | undefined) => void = () => {}
) {
  const handleError = (reason) => {
    setLoading(false)
    setError(reason)
  }

  if (file.content instanceof File && !file.uploaded) {
    const token = getCookie('access_token')

    const bodyRequest = {
      filename: file.content.name,
      contentLength: file.content.size,
      namespace: namespace,
    }

    setLoading(true)

    try {
      const { data, status } = await axios.post(
        `${process.env.NEXT_PUBLIC_BACKEND_API_BASE}/_/upload-url`,
        JSON.stringify(bodyRequest),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal,
        }
      )

      if (status !== 200) {
        handleError(`HTTP error! Status: ${status}`)
        console.error(`HTTP error! Status: ${status}`)
      }

      if (data) {
        await axios.put(data['presigned-url'], file.content, {
          headers: {
            'Content-type': 'text',
            'Access-Control-Allow-Origin': '*',
          },
          onUploadProgress: (event: ProgressEvent) => {
            setProgress(
              event.lengthComputable
                ? (event.loaded / event.total) * 100
                : undefined
            )
          },
          signal,
        })

        if (status === 200) {
          completeCallback(data['presigned-url'].split('?')[0])
          setLoading(false)
        } else {
          handleError(`HTTP error! Status: ${status}`)
          console.error(`HTTP error! Status: ${status}`)
        }
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        handleError(`Fetch error: ${error}`)
        console.error(error)
      }
    }
  }
}

const useUpload = (
  file: FileInCloud,
  namespace: string,
  completeCallback: Function
) => {
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState<number | undefined>(undefined)

  useEffect(() => {
    const controller = new AbortController()

    putPresignedObject(
      file,
      namespace,
      completeCallback,
      controller.signal,
      setError,
      setLoading,
      setProgress
    )

    return () => {
      // Cancel the request when the component unmounts
      controller.abort()
    }
  }, [file, namespace])

  return { uploaded: progress, loading, error }
}

export default useUpload
